"use client";
import { noop } from "lodash";
import { useEffect, useMemo, useState } from "react";

interface MaintenanceProgressProps {
  startTime?: string; // when maintenance started
  targetTime?: string; // when maintenance is expected to end
  onMaintenanceComplete?: () => void; // callback when maintenance is complete
}

export default function MaintenanceProgress(props: MaintenanceProgressProps) {
  const [progress, setProgress] = useState(0);

  // Define times with readable GMT format (using ISO 8601 with Z)
  const startTime = useMemo(
    () => (!!props.startTime ? new Date(props.startTime) : null),
    [props.startTime]
  ); // May 7, 2025 15:00:00 GMT

  const targetTime = useMemo(
    () => (!!props.targetTime ? new Date(props.targetTime) : null),
    [props.targetTime]
  ); // May 7, 2025 17:10:00 GMT

  useEffect(() => {
    if (!startTime || !targetTime) return;

    const START_TIME = startTime.getTime(); // ms since epoch (UTC)
    const TARGET_TIME = targetTime.getTime();
    const updateProgress = () => {
      const now = Date.now(); // UTC
      const total = TARGET_TIME - START_TIME;
      const elapsed = now - START_TIME;
      const percent = Math.min(100, Math.max(0, (elapsed / total) * 100));
      setProgress(percent);

      if (percent >= 100) {
        props.onMaintenanceComplete?.();
      }
    };

    updateProgress();
    const interval = setInterval(updateProgress, 100);
    return () => clearInterval(interval);
  }, [props, startTime, targetTime]);

  if (!startTime || !targetTime) return null;

  const startDateStr = startTime.toUTCString().replace("GMT", "UTC");
  const targetDateStr = targetTime.toUTCString().replace("GMT", "UTC");

  return (
    <div className="flex flex-col items-center justify-center">
      <div className="w-80 h-4 mt-4 bg-gray-800 rounded-full overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500 transition-all duration-100 ease-linear"
          style={{ width: `${progress}%` }}
        />
        <p className="text-sm text-gray-500 mb-6">
          Maintenance Window: {startDateStr} – {targetDateStr}
        </p>
      </div>
      <div className="mt-2 text-sm text-gray-400">
        {progress.toFixed(1)}% complete
      </div>
    </div>
  );
}

export const Maintenance = ({
  targetTime,
  startTime,
  onMaintenanceComplete,
  ...rest
}: MaintenanceProgressProps) => {
  return (
    <section className="py-20">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-xxl-6 col-xl-7 col-lg-9 col-md-10">
            <div className="eg-error__contentx text-center">
              <img
                src="/assets/img/images/maintenance.png"
                alt="error"
                className="scale-75"
              />
              <h3 className="text-5xl">Under Maintenance</h3>
              <p className="text-lg pt-2">
                We&apos;re making improvements. Please check back soon.
              </p>
            </div>
            {startTime && targetTime && (
              <MaintenanceProgress
                startTime={startTime}
                targetTime={targetTime}
                onMaintenanceComplete={onMaintenanceComplete || noop}
                {...rest}
              />
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
